import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Paper,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    Grid
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import api from '../../services/api';

const ImportRelationship = () => {
    const [clientsFile, setClientsFile] = useState(null);
    const [childrenFile, setChildrenFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const handleFileChange = (type) => (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'application/json') {
            if (type === 'clients') {
                setClientsFile(selectedFile);
            } else {
                setChildrenFile(selectedFile);
            }
            setError(null);
        } else {
            setError('Please select a valid JSON file');
            if (type === 'clients') {
                setClientsFile(null);
            } else {
                setChildrenFile(null);
            }
        }
    };

    const handleUpload = async () => {
        if (!clientsFile || !childrenFile) return;

        try {
            setLoading(true);
            setError(null);

            // Read both files
            const clientsContent = await clientsFile.text();
            const childrenContent = await childrenFile.text();
            
            const clients = JSON.parse(clientsContent);
            const children = JSON.parse(childrenContent);

            // Send to backend
            const response = await api.post('/admin/relationships/import', {
                clients,
                children
            });

            setResult(response.data);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to import relationships');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Import Parent-Child Relationships
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                    Upload JSON files containing client and children information to establish parent-child relationships.
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                            Clients File
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemText 
                                    primary="Child_Accounts" 
                                    secondary="Comma-separated list of child client IDs" 
                                />
                            </ListItem>
                        </List>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                            <Button
                                variant="contained"
                                component="label"
                                disabled={loading}
                            >
                                Select Clients File
                                <input
                                    type="file"
                                    hidden
                                    accept="application/json"
                                    onChange={handleFileChange('clients')}
                                />
                            </Button>
                            {clientsFile && (
                                <Typography variant="body2" color="textSecondary">
                                    Selected: {clientsFile.name}
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                            Children File
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemText 
                                    primary="Parent_Account" 
                                    secondary="Parent's client ID" 
                                />
                            </ListItem>
                        </List>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                            <Button
                                variant="contained"
                                component="label"
                                disabled={loading}
                            >
                                Select Children File
                                <input
                                    type="file"
                                    hidden
                                    accept="application/json"
                                    onChange={handleFileChange('children')}
                                />
                            </Button>
                            {childrenFile && (
                                <Typography variant="body2" color="textSecondary">
                                    Selected: {childrenFile.name}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {clientsFile && childrenFile && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                        sx={{ mt: 2 }}
                    >
                        Upload and Import
                    </Button>
                )}

                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Successfully processed {result.processed} relationships.
                        {result.linked > 0 && ` Linked ${result.linked} children to their parents.`}
                        {result.skipped > 0 && ` Skipped ${result.skipped} relationships.`}
                        {result.notFound > 0 && ` Could not find ${result.notFound} children.`}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default ImportRelationship; 